// Sorting and grouping for the case table inside a batch.
//
// Pure functions, deliberately separate from the view, for the same reason
// batchGrouping and batchFilter are: the ordering rules are testable without
// rendering, and a wrong order is invisible on screen.
//
// # The column list is the single source of truth
//
// CASE_COLUMNS drives the table header, the sort dropdown, and the sort itself.
// The batch list grew a Sort dropdown offering two fields while the table showed
// fourteen columns, and the two drifted because nothing tied them together. Here
// a column that exists is a column that sorts, and a test walks the list to
// prove none of them is inert.
//
// # Why grouping partitions cleanly here and not on the batch list
//
// A case has exactly ONE coder. batchGrouping's header explains at length why
// group-by-coder was removed there — a batch is worked by three to five coders,
// so it lands in several groups and the totals double-count. That does not apply
// to a case, so these groups are a true partition and their counts are exact.

import { caseLabel } from "../data/caseIdentity";
import { coderIdentity } from "../data/coders";
import type { AuditBatchCaseSummary } from "../types";

export type SortDirection = "asc" | "desc";

/** Columns the case table can sort by. */
export type CaseField =
  | "case"
  | "patient"
  | "serviceDate"
  | "facility"
  | "coder"
  | "payer"
  | "surgical"
  | "discrepancies"
  | "status";

/** Fields the case table can group by. */
export type CaseGroupField = "coder" | "status" | "facility";

export interface CaseColumn {
  field: CaseField;
  label: string;
  /** Header cell alignment; the numeric column is centred like its cells. */
  align?: "center";
  /** False for a column with nothing meaningful to order on. Every column is
   *  sortable today; the flag exists so adding one that is not does not
   *  silently produce a dead header. */
  sortable: boolean;
  /** Tooltip on the header, where the column needs one. */
  title?: string;
}

/**
 * The table's columns, in display order.
 *
 * Read by the header row, by the sort dropdown, and by compareOn below. Adding a
 * column here adds it to all three.
 */
export const CASE_COLUMNS: readonly CaseColumn[] = [
  { field: "case", label: "Case", sortable: true },
  { field: "patient", label: "Patient", sortable: true },
  { field: "serviceDate", label: "Service date", sortable: true },
  { field: "facility", label: "Facility", sortable: true },
  { field: "coder", label: "Coder", sortable: true },
  { field: "payer", label: "Payer", sortable: true },
  { field: "surgical", label: "Surgical CPT (A / B)", sortable: true },
  {
    field: "discrepancies",
    label: "Discrepancies",
    align: "center",
    sortable: true,
    title: "Cases with nothing to compare sort last, whichever way round.",
  },
  { field: "status", label: "Status", sortable: true },
];

/** Group-by options, in dropdown order. */
export const CASE_GROUP_FIELDS: readonly {
  field: CaseGroupField;
  label: string;
}[] = [
  { field: "coder", label: "Coder" },
  { field: "status", label: "Status" },
  { field: "facility", label: "Facility" },
];

/** The coder's display name for a case, or "" when no coder leg has arrived. */
function coderLabelOf(c: AuditBatchCaseSummary): string {
  if (!c.coderId) return "";
  // coderIdentity, not coderById: an unresolved FA id must not borrow a name
  // from the demo roster — the same rule the table's cells follow.
  return coderIdentity(c.coderId, c.episode?.coderName).name;
}

/**
 * The value a column sorts on.
 *
 * `undefined` means the case has NO value for the column — not an empty one.
 * The difference is load-bearing: an undefined value sorts last under both
 * directions (see compareOn), so a batch's uncoded cases collect at the end
 * instead of leading the list as a block of empty rows.
 */
function valueOf(
  c: AuditBatchCaseSummary,
  field: CaseField,
): string | number | undefined {
  switch (field) {
    case "case":
      return caseLabel(c);
    case "patient":
      // Sorts on the name only when the caller may see it. Without a grant
      // there is no name, and every row would tie — which is honest: the column
      // reads "Restricted" on all of them.
      return c.patient_context?.lastName || undefined;
    case "serviceDate":
      // The RAW FA string, which is an ISO timestamp and therefore sorts
      // correctly as a string. Never the display form: a locale-formatted date
      // sorts 8/7 after 8/18, which is the defect batchGrouping documents.
      return c.episode?.serviceStartDate || undefined;
    case "facility":
      // Sorted on what the column SHOWS. Ordering on the id would scatter
      // named facilities by a number the reader cannot see.
      return c.episode?.facilityName || c.episode?.facilityId || undefined;
    case "coder":
      return coderLabelOf(c) || undefined;
    case "payer":
      return c.payer || undefined;
    case "surgical":
      // The A slot, falling back to B. Symmetric: it orders on whichever code
      // the row leads with, and a row shows both, so this names no slot.
      return c.surgical.a || c.surgical.b || undefined;
    case "discrepancies":
      // Only a comparable case HAS a discrepancy figure. An uncomparable one
      // holds zero, and ordering that alongside a real zero would file "nothing
      // to compare" among "agreed on everything" — the confusion the agreement
      // badge exists to prevent.
      return c.comparable ? c.discrepancyCount : undefined;
    case "status":
      return c.status;
  }
}

function compareOn(
  a: AuditBatchCaseSummary,
  b: AuditBatchCaseSummary,
  field: CaseField,
  sign: number,
): number {
  const av = valueOf(a, field);
  const bv = valueOf(b, field);
  // Absent values sort last REGARDLESS of direction, so `sign` is deliberately
  // not applied. Reversing the order should bring the other end of the data to
  // the top, not a block of rows that have no value at all.
  if (av === undefined && bv === undefined) return 0;
  if (av === undefined) return 1;
  if (bv === undefined) return -1;
  const primary =
    typeof av === "number" && typeof bv === "number"
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true });
  return primary * sign;
}

/**
 * Sorts a batch's cases.
 *
 * The tiebreak on the case label is not cosmetic: without it, rows sharing a
 * value reorder between renders as the underlying array changes, and a reviewer
 * loses their place in a list they were scrolling.
 */
export function sortCases(
  cases: readonly AuditBatchCaseSummary[],
  field: CaseField,
  direction: SortDirection,
): AuditBatchCaseSummary[] {
  const sign = direction === "asc" ? 1 : -1;
  return [...cases].sort((a, b) => {
    const primary = compareOn(a, b, field, sign);
    if (primary !== 0) return primary;
    // The tiebreak is ASCENDING in both directions — it exists for stability,
    // and flipping it with the sort would reshuffle tied rows on every toggle.
    return caseLabel(a).localeCompare(caseLabel(b), undefined, {
      numeric: true,
    });
  });
}

export interface CaseGroup {
  /** Stable key for React and for tests. */
  key: string;
  label: string;
  cases: AuditBatchCaseSummary[];
}

/** Label for the bucket of cases that have no value for the grouping field. */
const GROUP_NONE: Record<CaseGroupField, string> = {
  coder: "Not coded yet",
  status: "Unknown",
  facility: "No facility recorded",
};

function groupKeyOf(
  c: AuditBatchCaseSummary,
  field: CaseGroupField,
): { key: string; label: string } | null {
  switch (field) {
    case "coder": {
      if (!c.coderId) return null;
      // Keyed on the ID, labelled with the name. Two coders can share a display
      // name, and keying on the label would merge their work into one group.
      return { key: c.coderId, label: coderLabelOf(c) || c.coderId };
    }
    case "status":
      return {
        key: c.status,
        label: c.status === "submitted" ? "Submitted" : "Unreviewed",
      };
    case "facility": {
      // Keyed on the ID, labelled with the name: two facilities can share a
      // name across clients, and keying on the label would merge them.
      const f = c.episode?.facilityId;
      if (!f) return null;
      return { key: f, label: c.episode?.facilityName || f };
    }
  }
}

/**
 * Groups a batch's cases.
 *
 * A true partition: every case lands in exactly one group, so a group's count is
 * simply its length and the counts sum to the batch. Cases with no value for the
 * field collect in a trailing "none" group rather than being filed under an
 * arbitrary owner or dropped — early in a batch's life that group is most of it.
 *
 * Group order follows the incoming (already sorted) order of their first case,
 * so changing the sort reorders the groups with it.
 */
export function groupCases(
  cases: readonly AuditBatchCaseSummary[],
  field: CaseGroupField,
): CaseGroup[] {
  const groups = new Map<string, CaseGroup>();
  const none: CaseGroup = {
    key: "__none__",
    label: GROUP_NONE[field],
    cases: [],
  };

  for (const c of cases) {
    const g = groupKeyOf(c, field);
    if (!g) {
      none.cases.push(c);
      continue;
    }
    const existing = groups.get(g.key);
    if (existing) {
      existing.cases.push(c);
    } else {
      groups.set(g.key, { key: g.key, label: g.label, cases: [c] });
    }
  }

  const out = [...groups.values()];
  // Last, always. It is the group with nothing in common except an absence, and
  // leading with it buries the work a reviewer came to do.
  if (none.cases.length > 0) out.push(none);
  return out;
}
